import React from 'react';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import AlertBanner from '../components/AlertBanner';
import NewsCard from '../components/NewsCard';
import { alerts, articles } from '../data/mockData';

const Home = () => {
  const featuredArticle = articles[0];
  const recentArticles = articles.slice(1);
  const recentAlerts = alerts.slice(0, 3);

  return (
    <div className="min-h-screen bg-background">
      <AlertBanner alerts={alerts} />

      {/* Hero / Featured Section */}
      <section className="bg-white border-b border-gray-100 py-6 md:py-12">
        <div className="container-custom">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-6 w-1.5 bg-primary rounded-full"></div>
            <h2 className="text-xs md:text-sm font-black text-gray-900 uppercase tracking-[0.2em]">Top Story</h2>
          </div>
          <NewsCard article={featuredArticle} featured={true} />
        </div>
      </section>

      <div className="container-custom py-10 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          
          {/* Main Content Column */}
          <div className="lg:col-span-8 space-y-12 md:space-y-16">
            
            {/* Real-time Alerts Section (Moved to top of main column) */}
            <section className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
              <div className="bg-alert/5 border-b border-alert/10 p-5 md:p-6 flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-alert/10 flex items-center justify-center">
                    <SafeIcon icon={FiIcons.FiAlertTriangle} className="text-alert text-xl" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 tracking-tight text-lg">Real-time Safety Alerts</h3>
                    <p className="text-xs text-gray-500 font-medium">Verified recall and outbreak notifications</p>
                  </div>
                </div>
                <Link to="/alerts" className="hidden sm:flex items-center gap-1 text-xs font-bold text-alert uppercase tracking-wider hover:underline">
                  View All <SafeIcon icon={FiIcons.FiArrowRight} />
                </Link>
              </div>
              
              <div className="divide-y divide-gray-100">
                {recentAlerts.map(alert => (
                  <a 
                    key={alert.id} 
                    href={alert.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex flex-col md:flex-row md:items-center gap-4 p-5 md:p-6 hover:bg-gray-50 transition-colors group"
                  >
                    <div className="flex items-center gap-2 shrink-0">
                      <span className={`text-[10px] font-black uppercase px-2 py-1 rounded tracking-tight ${
                        alert.severity === 'High' ? 'bg-alert text-white' : 'bg-yellow-400 text-gray-900'
                      }`}>
                        {alert.type}
                      </span>
                      <span className="text-[11px] text-gray-400 font-medium md:hidden">{alert.date}</span>
                    </div>
                    
                    <div className="flex-1">
                      <h4 className="text-sm md:text-base font-bold text-gray-800 group-hover:text-primary transition-colors leading-snug mb-1">
                        {alert.title}
                      </h4>
                      <p className="text-xs text-gray-500 line-clamp-1">{alert.summary}</p>
                    </div>

                    <div className="hidden md:flex flex-col items-end shrink-0 gap-1">
                      <span className="text-[11px] text-gray-400 font-medium">{alert.date}</span>
                      <SafeIcon icon={FiIcons.FiExternalLink} className="text-gray-300 group-hover:text-primary transition-colors text-sm" />
                    </div>
                  </a>
                ))}
              </div>
              
              <div className="p-4 bg-gray-50/50 border-t border-gray-100 sm:hidden">
                <Link to="/alerts" className="flex items-center justify-center w-full text-xs font-bold text-alert uppercase tracking-wider py-2">
                  View All Alerts <SafeIcon icon={FiIcons.FiChevronRight} />
                </Link>
              </div>
            </section>

            {/* Latest News */}
            <section>
              <div className="flex justify-between items-center mb-8 border-b border-gray-200 pb-4">
                <h2 className="text-xl md:text-2xl font-bold text-gray-900">Latest Reports</h2>
                <Link to="/resources" className="text-xs md:text-sm text-primary font-bold uppercase tracking-wider hover:text-accent flex items-center gap-1">
                  View Archives <SafeIcon icon={FiIcons.FiChevronRight} />
                </Link>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                {recentArticles.map(article => (
                  <NewsCard key={article.id} article={article} />
                ))}
              </div>
            </section>

            {/* Mission / CTA Section */}
            <section className="bg-primary-dark text-white rounded-2xl overflow-hidden shadow-xl relative">
              <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-white/5 to-transparent pointer-events-none"></div>
              <div className="relative z-10 p-8 md:p-12 lg:p-16">
                <div className="max-w-xl">
                  <div className="inline-flex items-center gap-2 bg-accent/20 text-accent-light px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest mb-6 border border-accent/30">
                    Our Mission
                  </div>
                  <h2 className="text-2xl md:text-4xl font-bold mb-6 leading-tight">Empowering Consumers with Independent Safety Data</h2>
                  <p className="text-gray-300 text-base md:text-lg mb-8 leading-relaxed">
                    As moringa supplements grow in popularity, so do the risks of adulteration and contamination. 
                    We provide the transparency needed to make safe choices.
                  </p>
                  <div className="flex flex-wrap gap-4">
                    <Link to="/about" className="bg-white text-primary-dark px-8 py-3 rounded-lg font-bold transition-all hover:bg-gray-100 shadow-lg active:scale-95">
                      How We Work
                    </Link>
                    <button className="bg-transparent border-2 border-white/20 text-white px-8 py-3 rounded-lg font-bold hover:bg-white/10 transition-all">
                      Report a Symptom
                    </button>
                  </div>
                </div>
              </div>
              <SafeIcon 
                icon={FiIcons.FiShield} 
                className="absolute -right-8 -bottom-8 text-[200px] md:text-[300px] text-white opacity-[0.03] rotate-12" 
              />
            </section>
          </div>

          {/* Sidebar */}
          <aside className="lg:col-span-4 space-y-8">
            
            {/* Newsletter Subscription */}
            <div className="bg-accent-light/10 border border-accent/20 p-6 md:p-8 rounded-xl">
              <h3 className="font-bold text-gray-900 text-lg mb-2">Stay Protected</h3>
              <p className="text-sm text-gray-600 mb-6 leading-relaxed">Join 12,000+ subscribers receiving urgent recall notifications.</p>
              <form className="space-y-3" onSubmit={(e) => e.preventDefault()}>
                <input 
                  type="email" 
                  placeholder="name@email.com" 
                  className="w-full px-4 py-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary bg-white shadow-inner"
                />
                <button className="w-full bg-primary text-white text-sm font-bold py-3 rounded-lg shadow-md hover:bg-primary-light transition-all active:scale-95">
                  Sign Up for Alerts
                </button>
              </form>
              <p className="text-[10px] text-gray-400 mt-4 text-center italic">
                Independent reporting. No spam. Unsubscribe anytime.
              </p>
            </div>

            {/* Resource Hub Link Card */}
            <div className="bg-white border border-gray-200 p-6 rounded-xl flex items-center gap-4 group cursor-pointer hover:border-primary transition-all">
              <div className="w-12 h-12 rounded-full bg-primary/5 flex items-center justify-center group-hover:bg-primary/10 transition-colors">
                <SafeIcon icon={FiIcons.FiBookOpen} className="text-primary text-xl" />
              </div>
              <div>
                <h4 className="font-bold text-gray-900 text-sm">Educational Hub</h4>
                <p className="text-xs text-gray-500">Guides, lab reports & testing data</p>
              </div>
            </div>

            {/* State Resources (New Sidebar Widget to fill gap) */}
            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                <SafeIcon icon={FiIcons.FiMapPin} className="text-primary" />
                State Resources
              </h3>
              <ul className="space-y-3 text-sm">
                <li>
                  <a href="https://dph.illinois.gov" target="_blank" className="text-gray-600 hover:text-primary flex items-center justify-between">
                    Illinois Health Dept <SafeIcon icon={FiIcons.FiExternalLink} className="text-[10px]" />
                  </a>
                </li>
                <li>
                  <a href="https://doh.wa.gov" target="_blank" className="text-gray-600 hover:text-primary flex items-center justify-between">
                    Washington Health Dept <SafeIcon icon={FiIcons.FiExternalLink} className="text-[10px]" />
                  </a>
                </li>
              </ul>
            </div>

          </aside>
        </div>
      </div>
    </div>
  );
};

export default Home;